import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Crown, Undo2, Swords, Video, Lock, Unlock, XCircle, Flag, Check, Repeat, MapPin } from "lucide-react";
import { labelLineFormat, categoryLabel } from "@/lib/clubwars";
import { KitchenZone } from "@/components/umpire/LiveScoring";
import PreMatchWizard from "@/components/umpire/PreMatchWizard";
import api, { formatApiError } from "@/lib/api";

export default function CwUmpireScore() {
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [err, setErr] = useState(false);
  const [serving, setServing] = useState("a");
  const [pip, setPip] = useState(false);
  const [swapped, setSwapped] = useState(false);
  const [sideAlert, setSideAlert] = useState(false);
  const busyRef = useRef(false);
  const prevServing = useRef(null);
  const alertedAt = useRef(null);

  const load = useCallback(() => {
    api.get("/cw-umpire/match").then(({ data }) => { setM(data); setErr(false); })
      .catch((e) => { if (e.response?.status === 401) setErr(true); });
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => { if (!busyRef.current) load(); }, 2500);
    return () => clearInterval(t);
  }, [load]);

  const sc = m && m.ready ? m.scorecard : null;

  useEffect(() => {
    if (!sc) return;
    if (prevServing.current && prevServing.current !== sc.serving_team && !sc.match_over) {
      toast.info("SIDE OUT — service changes", { duration: 2200 });
    }
    prevServing.current = sc.serving_team;
    const sideChangeAt = Math.ceil((sc.target + 1) / 2);
    const top = Math.max(sc.team_a_score, sc.team_b_score);
    if (top === sideChangeAt && alertedAt.current !== sideChangeAt && !sc.match_over) {
      alertedAt.current = sideChangeAt;
      setSideAlert(true);
      setSwapped((s) => !s);
    }
  }, [sc]);

  const call = async (path, body) => {
    busyRef.current = true;
    try { const { data } = await api.post(`/cw-umpire/${path}`, body); setM({ ready: true, ...data }); if (path === "commit") { alertedAt.current = null; prevServing.current = null; } }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { busyRef.current = false; }
  };

  if (err) return <Center>Your PIN session expired. <button className="text-primary-c font-bold ml-1" onClick={() => nav("/cw-umpire")}>Sign in again</button></Center>;
  if (!m) return <Center><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></Center>;
  if (!m.ready) return <Center>Waiting for both line-ups to be locked for<br /><span className="font-bold text-primary-c">{m.event_name}</span>.</Center>;

  const nameA = (m.a_player_names || []).join(" / ") || m.team_a_name;
  const nameB = (m.b_player_names || []).join(" / ") || m.team_b_name;
  const isFinal = m.status === "final";
  const firstSet = (m.sets || []).length === 0;
  const header = (
    <div className="mb-3">
      <div className="flex items-center gap-2 mb-1"><Swords className="w-5 h-5 text-primary-c" /><span className="text-xs tracking-wider uppercase font-bold text-primary-c">{m.event_name}</span></div>
      <h1 className="font-display font-bold text-xl">{categoryLabel({ type: m.line_type, skill: m.skill, entry_label: m.entry_label })} <span className="text-xs font-normal" style={{ color: "var(--text-2)" }}>· {labelLineFormat(m.format)}</span></h1>
      <div className="text-sm mt-1" style={{ color: "var(--text-2)" }}>{nameA} vs {nameB}{m.court ? ` · Court ${m.court}` : ""}</div>
      {(m.sets || []).length > 0 && <div className="mt-1 text-xs mono" style={{ color: "var(--text-2)" }}>sets: {m.sets.map((s) => `${s.a}-${s.b}`).join(", ")}</div>}
      {m.setup?.challenge_court && <span className="mt-2 inline-flex items-center gap-1.5 text-[10px] mono uppercase glass rounded-full px-2.5 py-1 text-primary-c"><Video className="w-3.5 h-3.5" /> Challenge court</span>}
    </div>
  );

  // First set, not started → full tournament Pre-Match Wizard
  if (!isFinal && !sc && firstSet) {
    return (
      <Shell>
        {header}
        <PreMatchWizard aName={nameA} bName={nameB} courtName={m.court ? `Court ${m.court}` : ""}
          onLaunch={(cfg) => call("start", { serving_team: cfg.first_server_team || "a", setup: cfg.setup })} launching={false} />
      </Shell>
    );
  }

  const order = swapped ? ["b", "a"] : ["a", "b"];
  const zones = {
    a: <KitchenZone key="a" side="a" name={nameA} score={sc?.team_a_score} serving={sc?.serving_team === "a"} position={sc?.server_position}
      isWinner={sc?.winner === "a"} locked={pip} faults={sc?.fault_count} faultsTotal={sc?.faults_to_sideout} band={sc?.setup?.serving_bands?.a} onScore={() => call("rally", { action: "POINT" })} />,
    b: <KitchenZone key="b" side="b" name={nameB} score={sc?.team_b_score} serving={sc?.serving_team === "b"} position={sc?.server_position}
      isWinner={sc?.winner === "b"} locked={pip} faults={sc?.fault_count} faultsTotal={sc?.faults_to_sideout} band={sc?.setup?.serving_bands?.b} onScore={() => call("rally", { action: "POINT" })} />,
  };

  return (
    <Shell>
      {header}
      {m.court && <div className="flex items-center justify-center gap-1.5 text-xs mono uppercase tracking-wider text-primary-c mb-3" data-testid="cw-umpire-court"><MapPin className="w-3.5 h-3.5" /> Court {m.court}</div>}

      {isFinal ? (
        <div className="rounded-3xl glow-border p-10 text-center" style={{ background: "rgba(34,197,94,0.10)" }} data-testid="cw-umpire-final">
          <Crown className="w-10 h-10 mx-auto" style={{ color: "#eab308" }} />
          <div className="font-display font-black text-2xl mt-2">{m.winner === "a" ? nameA : nameB} wins</div>
          <div className="mono text-sm mt-1" style={{ color: "var(--text-2)" }}>{m.sets.map((s) => `${s.a}-${s.b}`).join(", ")}</div>
        </div>
      ) : !sc ? (
        <div className="rounded-3xl glow-border p-6 text-center space-y-4" style={{ background: "var(--surface)" }} data-testid="cw-umpire-nextset">
          <p className="text-sm" style={{ color: "var(--text-2)" }}>Who serves first this set?</p>
          <div className="flex gap-2 justify-center">
            <button onClick={() => setServing("a")} className="rounded-full px-4 py-2 text-sm font-bold" style={serving === "a" ? { background: "var(--primary)", color: "var(--on-primary)" } : { background: "var(--bg)", color: "var(--text-2)" }}>{nameA}</button>
            <button onClick={() => setServing("b")} className="rounded-full px-4 py-2 text-sm font-bold" style={serving === "b" ? { background: "var(--primary)", color: "var(--on-primary)" } : { background: "var(--bg)", color: "var(--text-2)" }}>{nameB}</button>
          </div>
          <button data-testid="cw-umpire-start" onClick={() => call("start", { serving_team: serving })} className="btn-glow rounded-2xl px-6 py-3 font-bold mx-auto">Start next set</button>
        </div>
      ) : (
        <div data-testid="cw-umpire-rally">
          <div className="rounded-3xl glow-border p-5 mb-4 text-center" style={{ background: "var(--surface)" }} data-testid="cw-score-call-panel">
            <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-2)" }}>Score call</div>
            <div className="font-display font-black tracking-tighter leading-none mt-1" style={{ fontSize: "3rem" }} data-testid="cw-score-call">{sc.score_call}</div>
            <div className="text-xs mt-2" style={{ color: "var(--text-2)" }}>Serving: <span className="text-primary-c font-bold">{sc.serving_team === "a" ? nameA : nameB}</span>{sc.discipline === "doubles" ? ` · server ${sc.server_number}` : ""} · {sc.server_position} court</div>
            <div className="text-[10px] mono uppercase tracking-wider mt-1" style={{ color: "var(--text-2)" }}>game to {sc.target} · win by {sc.win_by}{sc.cap ? ` · cap ${sc.cap}` : ""}</div>
          </div>

          <button data-testid="cw-pip-toggle" onClick={() => setPip((v) => !v)} className="w-full rounded-2xl py-3 mb-3 font-black tracking-wider flex items-center justify-center gap-2"
            style={pip ? { background: "rgba(239,68,68,0.16)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.5)" } : { background: "rgba(0,229,255,0.1)", color: "var(--primary-ink)", border: "1px solid rgba(0,229,255,0.4)" }}>
            {pip ? <><Lock className="w-4 h-4" /> POINT IN PROGRESS — LOCKED</> : <><Unlock className="w-4 h-4" /> PIP · tap to lock during rally</>}
          </button>

          <div className="flex items-center justify-center gap-2 mb-2 text-[10px] mono uppercase tracking-wider" style={{ color: "var(--text-2)" }}>
            <span>{swapped ? "Right" : "Left"} end</span>
            <button data-testid="cw-swap-sides" onClick={() => setSwapped((s) => !s)} className="flex items-center gap-1 glass rounded-full px-2.5 py-1" style={{ color: "var(--primary-ink)" }}><Repeat className="w-3 h-3" /> swap ends</button>
            <span>{swapped ? "Left" : "Right"} end</span>
          </div>
          <div className="flex gap-3" style={{ opacity: pip ? 0.5 : 1 }}>{order.map((s) => zones[s])}</div>

          {sc.explanation && <p className="text-center text-xs mt-3" style={{ color: "var(--text-2)" }} data-testid="cw-rally-explanation">{sc.explanation}</p>}

          {sc.match_over ? (
            <button data-testid="cw-umpire-commit" onClick={() => call("commit")} className="btn-glow w-full rounded-2xl py-4 mt-3 font-bold flex items-center justify-center gap-2"><Check className="w-5 h-5" /> Commit set ({sc.team_a_score}-{sc.team_b_score})</button>
          ) : (
            <button data-testid="cw-umpire-fault" onClick={() => call("rally", { action: "FAULT" })} disabled={pip} className="w-full rounded-2xl py-4 mt-3 font-black tracking-wider flex items-center justify-center gap-2 disabled:opacity-40"
              style={{ background: "rgba(239,68,68,0.16)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.4)" }}>
              <XCircle className="w-5 h-5" /> FAULT <span className="text-[10px] font-medium normal-case tracking-normal opacity-80">(auto side-out on 2nd)</span>
            </button>
          )}

          <div className="flex items-center justify-center mt-4">
            <button data-testid="cw-umpire-undo" onClick={() => call("undo")} disabled={!sc.can_undo} className="glass rounded-full px-4 py-2 text-xs font-bold flex items-center gap-1.5 disabled:opacity-40" style={{ color: "var(--text)" }}><Undo2 className="w-3.5 h-3.5" /> Undo</button>
          </div>
        </div>
      )}

      <AnimatePresence>
        {sideAlert && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.7)" }} data-testid="cw-side-change-alert" onClick={() => setSideAlert(false)}>
            <motion.div initial={{ scale: 0.85 }} animate={{ scale: 1 }} exit={{ scale: 0.85 }} className="rounded-3xl glow-border p-8 text-center max-w-sm" style={{ background: "var(--surface)" }}>
              <Repeat className="w-12 h-12 mx-auto text-primary-c" />
              <div className="font-display font-black text-3xl mt-4">SIDE CHANGE</div>
              <p className="text-sm mt-2" style={{ color: "var(--text-2)" }}>Halfway point reached — players switch ends.</p>
              <button onClick={() => setSideAlert(false)} className="btn-glow rounded-full px-6 py-2.5 mt-6 font-bold">Got it</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <div className="max-w-lg mx-auto px-4 py-8 grain" data-testid="cw-umpire-console">{children}</div>
    </div>
  );
}

function Center({ children }) {
  return <div className="min-h-screen flex items-center justify-center text-center text-sm px-6" style={{ background: "var(--bg)", color: "var(--text-2)" }}>{children}</div>;
}
